import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Link2, Power, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/auth";
import { isTemplated } from "@/components/admin/DemoTemplateSettings";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function Status({ on, label }) {
  return <span className={`px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-[0.14em] ${on ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-slate-500"}`}>{label}</span>;
}

function LinkRow({ l, onToggle, onDelete }) {
  const url = `${window.location.origin}/d/${l.token}`;
  const [copied, setCopied] = useState(false);
  const copy = () => { try { navigator.clipboard?.writeText(url)?.catch?.(() => {}); } catch {} setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <li data-testid={`share-link-row-${l.id}`} className={`rounded-xl border p-4 flex flex-wrap items-center gap-4 ${l.active ? "border-white/10 bg-white/[0.03]" : "border-white/5 bg-white/[0.01] opacity-60"}`}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-white">{l.director_name} {l.organization && <span className="text-slate-400 font-normal">· {l.organization}</span>}</div>
        <div className="mt-1 font-mono text-xs text-brand-cyan truncate" data-testid={`share-link-url-${l.id}`}>{url}</div>
        <div className="mt-2 flex flex-wrap gap-2 items-center text-[11px] text-slate-400">
          <span data-testid={`share-link-opens-${l.id}`}>Opened {l.open_count}×</span>
          <span>First {fmt(l.first_opened_at)}</span>
          <span>Last {fmt(l.last_opened_at)}</span>
          <Status on={!!l.finished_at} label={l.finished_at ? "Finished tour" : "Not finished"} />
          <Status on={!!l.pilot_booked_at} label={l.pilot_booked_at ? "Pilot booked" : "No pilot yet"} />
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={copy} data-testid={`share-link-copy-${l.id}`} className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors" aria-label="Copy link">
          {copied ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
        </button>
        <button type="button" onClick={() => onToggle(l)} data-testid={`share-link-toggle-${l.id}`} className={`p-2 rounded-lg transition-colors ${l.active ? "text-emerald-300 hover:bg-emerald-500/10" : "text-slate-500 hover:bg-white/5"}`} aria-label={l.active ? "Turn off link" : "Turn on link"}>
          <Power className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onDelete(l)} data-testid={`share-link-delete-${l.id}`} className="p-2 rounded-lg text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors" aria-label="Delete link">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}

// Personal links — one per director, skips the access code and opens straight into the auto tour.
export default function ShareLinksPanel({ demos }) {
  const options = useMemo(() => demos.filter(isTemplated), [demos]);
  const [slug, setSlug] = useState("");
  const [form, setForm] = useState({ director_name: "", organization: "" });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!slug && options[0]) setSlug(options[0].slug); }, [options, slug]);

  const reload = () => apiClient.get("/admin/share-links").then(({ data }) => setItems(data.items || [])).catch(() => {});
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.director_name.trim()) return toast.error("Director name is required");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/share-links", { slug, ...form });
      navigator.clipboard?.writeText(`${window.location.origin}/d/${data.token}`)?.catch?.(() => {});
      toast.success("Link created and copied");
      setForm({ director_name: "", organization: "" });
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create link"); }
    finally { setBusy(false); }
  };
  const toggle = async (l) => { await apiClient.patch(`/admin/share-links/${l.id}`, { active: !l.active }); reload(); };
  const remove = async (l) => { if (window.confirm(`Delete ${l.director_name}'s link?`)) { await apiClient.delete(`/admin/share-links/${l.id}`); reload(); } };

  const input = "rounded-lg bg-navy-950/60 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors";
  return (
    <div data-testid="share-links-panel" className="mt-8 rounded-2xl border border-white/10 bg-navy-900/60 p-5">
      <div className="flex items-center gap-2 text-brand-cyan font-mono text-[11px] tracking-[0.24em] uppercase"><Link2 className="w-3.5 h-3.5" /> // Personal links</div>
      <p className="mt-1 text-sm text-slate-400">Send a director a private link — no code, one tap, straight into the auto tour.</p>
      <form onSubmit={create} className="mt-4 flex flex-wrap gap-2">
        <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="share-link-demo-select" className={input}>
          {options.map((d) => <option key={d.slug} value={d.slug}>{d.business_name}</option>)}
        </select>
        <input value={form.director_name} onChange={(e) => setForm({ ...form, director_name: e.target.value })} placeholder="Director name" data-testid="share-link-director" className={`${input} flex-1 min-w-[160px]`} />
        <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="Organization (optional)" data-testid="share-link-org" className={`${input} flex-1 min-w-[160px]`} />
        <button type="submit" disabled={busy || !slug} data-testid="share-link-create" className="rounded-lg bg-gradient-to-r from-brand-blue to-brand-cyan px-4 py-2 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase text-navy-950 hover:brightness-110 transition disabled:opacity-50">Create link</button>
      </form>
      <ul className="mt-4 space-y-2">{items.map((l) => <LinkRow key={l.id} l={l} onToggle={toggle} onDelete={remove} />)}</ul>
      {items.length === 0 && <div data-testid="share-links-empty" className="mt-4 text-sm text-slate-500">No personal links yet.</div>}
    </div>
  );
}
